'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControl, MenuItem, Select } from '@mui/material';

import { TabButton } from '@/components';
import { useT } from '@/hooks';
import { TimeFilter } from '@/types';
import { formatCategoryDisplay, formatTimePeriodDisplay } from '@/utils';

interface LeaderboardFiltersProps {
  categories: string[];
  timePeriods: string[];
  selectedTimeFilter: TimeFilter;
  setSelectedTimeFilter: (val: TimeFilter) => void;
  selectedCategory: string;
  setSelectedCategory: (val: string) => void;
}

export const LeaderboardFilters = ({
  categories = [],
  timePeriods = [],
  selectedTimeFilter,
  setSelectedTimeFilter,
  selectedCategory,
  setSelectedCategory,
}: LeaderboardFiltersProps) => {
  const { t } = useT();
  const displayTimePeriods = timePeriods?.length > 0 ? timePeriods : ['DAY'];

  const renderFilter = (filter: TimeFilter) => {
    const isActive = selectedTimeFilter === filter;
    const label = formatTimePeriodDisplay(filter, t);
    const handleClick = () => setSelectedTimeFilter(filter);

    return <TabButton key={filter} label={label} isActive={isActive} onClick={handleClick} />;
  };

  const renderFilterList = displayTimePeriods?.map((f) => renderFilter(f as TimeFilter));

  const displayCategories = categories?.length > 0 ? categories : ['OVERALL'];

  const renderMobileTimePeriod = (filter: string) => (
    <MenuItem key={filter} value={filter} sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
      {formatTimePeriodDisplay(filter, t)}
    </MenuItem>
  );
  const mobileTimePeriodList = displayTimePeriods.map(renderMobileTimePeriod);

  const renderCategoryOption = (cat: string) => (
    <MenuItem key={cat} value={cat}>
      {formatCategoryDisplay(cat, t)}
    </MenuItem>
  );
  const categoryOptionList = displayCategories.map(renderCategoryOption);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Mobile Dropdown */}
        <FormControl size="small" sx={{ display: { xs: 'block', md: 'none' }, minWidth: 120 }}>
          <Select
            value={selectedTimeFilter}
            onChange={(e) => setSelectedTimeFilter(e.target.value as TimeFilter)}
            sx={{
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 600,
              height: 32,
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid',
                borderColor: 'grey.300',
              },
              '& .MuiSelect-select': {
                py: 0.75,
                fontWeight: 600,
              },
            }}
            IconComponent={KeyboardArrowDownIcon}
          >
            {mobileTimePeriodList}
          </Select>
        </FormControl>

        {/* Desktop Tabs */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexWrap: 'wrap' }}>
          {renderFilterList}
        </Box>
      </Box>

      {/* Category Dropdown */}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          sx={{
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: 600,
            height: 32,
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid',
              borderColor: 'grey.300',
            },
            '& .MuiSelect-select': {
              py: 0.75,
              fontWeight: 600,
            },
          }}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            disablePortal: true,
            disableScrollLock: true,
            PaperProps: {
              sx: {
                borderRadius: '6px',
                mt: 0.5,
                '& .MuiMenuItem-root': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
              },
            },
          }}
        >
          {categoryOptionList}
        </Select>
      </FormControl>
    </Box>
  );
};
